/**
 * Manages operations related to asset fields.
 * @class
 * @requires jQuery
 */
import { removeHost } from '../methods/removeHost.js';
import { getDataApiUrl } from '../methods/getDataApiUrl.js';

export class AssetField {
    /**
     * AssetField クラスを生成するためのコンストラクタ。
     *
     * @class
     * @param {string} id - アセットフィールドのID。この引数は必須です。
     * @param {string} [type='image'] - アセットのタイプ。デフォルトは 'image' です。
     * @param {string} handle - フィールドハンドル
     * @param {object} options - オプション
     * @param {number} options.thumbnailWidth - サムネイルの幅
     * @param {number} options.thumbnailHeight - サムネイルの高さ
     * @throws {Error} id パラメータが指定されていない場合、エラーをスローします。
     */
    constructor(id, type = 'image', handle = '', options = {}) {
        if (!id) {
            throw new Error('The "id" parameter is required as the first argument.');
        }
        this.id = id;
        this.type = type;
        this.handle = handle;
        this.thumbnailWidth = options.thumbnailWidth || 120;
        this.thumbnailHeight = options.thumbnailHeight || 120;
    }

    //----------------------------------------
    // 静的メソッド
    //----------------------------------------

    /**
     * AssetField クラスの初期化メソッド。
     * mtModal() を使用してモーダルを初期化し、'hidden.bs.modal.assetField' イベントのハンドラを設定します。
     *
     * @static
     */
    static init() {
        // アセットモーダルの中の処理
        AssetField.initAssetModalInnerContent();
        if (typeof window.top.mtappVars.__system.assetField === 'object') {
            return;
        }
        window.mtappVars.__system.assetField = window.mtappVars.__system.assetField || { uploadDirectories: {} };
        jQuery('#mtapp-asset-field').mtModal();
        const mtModal = document.querySelector('.mt-modal');
        mtModal.addEventListener('shown.bs.modal', AssetField.handleModalShown);
        mtModal.addEventListener('hidden.bs.modal', AssetField.handleModalHidden);
    }

    /**
     * アセットモーダルの中のiframeの中の処理
     */
    static initAssetModalInnerContent() {
        const params = new URLSearchParams(location.search);
        if (
            window.top !== window &&
            params.get('__mode') === 'list_asset' &&
            params.get('_type') === 'asset' &&
            params.get('dialog_view') === '1'
        ) {
            // モーダルの中のiframeが読み込まれた時の処理
            window.addEventListener('load', () => {
                const selectAssetForm = document.getElementById('select_asset');
                if (selectAssetForm) {
                    const selectAssetId = selectAssetForm.querySelector('input[name="id"]');
                    const insertButton = document.querySelector('button[type="submit"].btn-primary');
                    if (insertButton) {
                        insertButton.addEventListener('click', () => {
                            AssetField.transferAssetData(selectAssetId);
                        });
                    }
                }
                jQuery(document).on('mtappAssetFieldReady', function (event, data) {
                    const handle = data.mtappMultiFieldHandle;
                    const handleDir = window.top.mtappVars.__system.assetField.uploadDirectories[handle];
                    const extraPath = window.document.getElementById('extra_path');
                    if (!handle || typeof handleDir === 'undefined' || !extraPath) {
                        return;
                    }
                    if (typeof handleDir === 'string') {
                        extraPath.value = handleDir;
                    } else if (Array.isArray(handleDir)) {
                        // 新しい select 要素を作成
                        const selectElement = document.createElement('select');
                        selectElement.name = 'extra_path_select';
                        selectElement.classList.add('form-select');

                        // オプションを select に追加
                        handleDir.forEach(function (optionValue) {
                            const option = document.createElement('option');
                            option.value = optionValue;
                            option.text = optionValue;
                            selectElement.appendChild(option);
                        });

                        // input の直前に select を挿入
                        extraPath.parentNode.insertBefore(selectElement, extraPath);

                        // 元の input 要素を非表示にする
                        extraPath.style.display = 'none';

                        // selectElement が変更された時に選択された値を extraPath の value にセットするイベントリスナーを追加
                        selectElement.addEventListener('change', function () {
                            extraPath.value = this.value;
                        });

                        // 初期値を設定するために、イベントリスナー関数を手動で一度実行する
                        selectElement.dispatchEvent(new Event('change'));
                    }
                });
                jQuery(document).trigger('mtappAssetFieldReady', [{ mtappMultiFieldHandle: params.get('mtapp_mf_handle') }]);
            });
        }
    }
    /**
     * 指定された要素の値を設定し、'input' と 'blur' イベントをディスパッチします。
     *
     * @static
     * @param {HTMLInputElement} el - 値を設定する対象のDOM要素。
     * @param {string|number} value - 要素に設定する値。
     */
    static setElementValue(el, value) {
        if (el) {
            el.value = value;
            el.dispatchEvent(new Event('input'));
            el.dispatchEvent(new Event('blur'));
        }
    }

    /**
     * 指定されたURLからデータを非同期的に取得する。
     *
     * @static
     * @param {string} url - データを取得するためのAPIのURL。
     * @param {Object} [params={}] - APIリクエストに付随するパラメータ。
     * @returns {Promise} データの取得結果を保持するPromiseオブジェクト。
     */
    static async fetchDataAPI(url, params) {
        const queryParams = new URLSearchParams(params).toString();
        const response = await fetch(`${url}?${queryParams}`);
        if (!response.ok) {
            throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        return response.json();
    }

    /**
     * `mtappVars.__system.assetField`のプロパティを初期状態にリセットする。
     *
     * @static
     */
    static cleanup() {
        window.mtappVars.__system.assetField.assetId = '';
        window.mtappVars.__system.assetField.assetDetails = null;
        // window.mtappVars.__system.assetField.assetType = 'image';
    }

    /**
     * 「挿入」ボタンクリック時に、選択されたアセットのデータを親ウィンドウに転送する。
     * Chrome の unload イベント廃止に伴い、ライフサイクルイベントに依存しない方式に変更。
     *
     * @static
     * @param {HTMLInputElement} selectAssetId - アセットIDを保持する hidden input 要素
     */
    static transferAssetData(selectAssetId) {
        let selectedValue = selectAssetId && selectAssetId.value;

        // フォールバック:
        // MT 標準は「挿入」ボタンの click ハンドラ内で #select_asset input[name="id"] に
        // 選択 ID をセットする。本処理も同じ click イベントを購読しているため、リスナーの
        // 実行順序によっては値がセットされる前に読み取ってしまい空になる（環境依存で再現）。
        // その場合でも確実に動くよう、選択済みのチェックボックス/ラジオから直接 ID を取得する。
        if (!selectedValue) {
            try {
                const checkedEls = window.document.querySelectorAll(
                    '#asset-table input[name="id"]:checked, table.listing-table tbody input[name="id"]:checked'
                );
                if (checkedEls.length > 0) {
                    // multiField の画像/ファイルは単一選択想定。最初の 1 件を採用。
                    selectedValue = checkedEls[0].value;
                }
            } catch {
                // 取得失敗時はそのまま下の早期 return に委ねる
            }
        }

        // フォールバック2:
        // ダイアログ内でその場アップロード（ドラッグ＆ドロップ）した新規アセットは、
        // アセット一覧の選択（checked ラジオ）には現れない。MT がアップロード直後の ID を
        // #select_asset へセットしない / セットが間に合わない環境では上記でも取得できないため、
        // アップロード結果パネル（#upload-progress）から直接 data-asset-id を取得する。
        if (!selectedValue) {
            try {
                const uploaded = window.document.querySelector('#upload-progress [data-asset-id]');
                if (uploaded) {
                    selectedValue = uploaded.getAttribute('data-asset-id');
                }
            } catch {
                // 取得失敗時はそのまま下の早期 return に委ねる
            }
        }

        if (!selectedValue) {
            // ここで ID が取れないとフィールドへの反映が一切行われない（無言で失敗する）ため、
            // 調査の手がかりとして必ず警告を残す。
            console.warn(
                '[AssetField] 挿入対象のアセットIDを取得できませんでした（select_asset 未設定・一覧未選択・アップロード結果なし）。'
            );
            return;
        }

        window.top.mtappVars.__system.assetField.assetId = selectedValue;

        const assetJson = window.document.getElementById(`asset-${selectedValue}-json`);
        if (assetJson) {
            window.top.mtappVars.__system.assetField.assetDetails = JSON.parse(assetJson.value);
            return;
        }

        // アップロード結果パネルから URL・サムネイルを取得する（Data API 非依存）。
        // 新規アップロードのアセットは asset-{id}-json（メタデータ）が無い環境があるため、
        // パネル内のサムネイル img と原寸リンクから直接取得し、プレビューを確実に表示する。
        const uploadProgress = window.document.getElementById('upload-progress');
        if (uploadProgress) {
            const uploadedAsset = uploadProgress.querySelector(`[data-asset-id="${selectedValue}"]`);
            if (uploadedAsset) {
                const assetLink = uploadedAsset.querySelector('a[target="_blank"]');
                const thumbImg = uploadedAsset.querySelector('.upload-thumbnail img, img.img-thumbnail');
                window.top.mtappVars.__system.assetField.assetDetails = {
                    url: assetLink ? assetLink.getAttribute('href') : '',
                    thumbnail_url: thumbImg ? thumbImg.getAttribute('src') : ''
                };
                return;
            }
        }
    }

    /**
     * モーダルが表示された際のハンドラー。
     *
     * @static
     */
    static handleModalShown() {
        AssetField.cleanup();
    }

    /**
     * MT の metadata_json に含まれるサムネイル URL を取り出す。
     * メディア一覧から選択した場合、Data API を呼ばずに即座にサムネイル URL が決まるため、
     * await を跨ぐ非同期処理を完全に排除できる（race condition の根本対策）。
     *
     * @static
     * @param {Object|null} assetDetails - asset の metadata_json をパースしたオブジェクト
     * @param {number} width  - 要求するサムネイルの幅
     * @param {number} height - 要求するサムネイルの高さ
     * @returns {string|null} サムネイル URL（取得できなければ null）
     */
    static pickThumbFromMetadata(assetDetails, width, height) {
        if (!assetDetails) return null;
        // 要求寸法と一致するときのみ採用する。
        // 寸法不一致の thumbnail_url（ダイアログ内アップロード直後の固定 60x60 等）を
        // 採用してしまうと、メディア一覧から選んだ場合（指定寸法）と
        // プレビューのサイズが食い違う。揃えるため、不一致時は Data API 取得へ委ねる。
        if (
            assetDetails.thumbnail_url &&
            Number(assetDetails.thumbnail_width) === Number(width) &&
            Number(assetDetails.thumbnail_height) === Number(height)
        ) {
            return assetDetails.thumbnail_url;
        }
        return null;
    }

    /**
     * Data API でサムネイル URL を取得する。タイムアウトと失敗時フォールバックを保証。
     * fetch が pending のまま戻らなくても TIMEOUT_MS で abort し、fallbackUrl を返す。
     *
     * @static
     * @returns {Promise<string>} サムネイル URL（失敗時は fallbackUrl）
     */
    static async fetchThumbnailWithFallback(apiUrl, siteId, assetId, width, height, fallbackUrl) {
        const TIMEOUT_MS = 5000;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
        const thumbnailUrl = `${apiUrl}/sites/${siteId}/assets/${assetId}/thumbnail?width=${width}&height=${height}`;
        window.dispatchEvent(new CustomEvent('mtapp:thumbnailRequested', { detail: { url: thumbnailUrl, siteId, width, height, assetId } }));
        try {
            const res = await fetch(thumbnailUrl, { signal: ctrl.signal });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            window.dispatchEvent(new CustomEvent('mtapp:thumbnailResponse', { detail: { ok: true, data, assetId } }));
            if (data.error || !data.url) {
                throw new Error((data.error && data.error.message) || 'thumbnail url not returned');
            }
            return data.url;
        } catch (e) {
            window.dispatchEvent(new CustomEvent('mtapp:thumbnailFallbackApplied', { detail: { url: fallbackUrl, reason: String(e && e.message || e), assetId } }));
            return fallbackUrl;
        } finally {
            clearTimeout(timer);
        }
    }

    /**
     * Data API でアセットの公開 URL を取得する。タイムアウトと失敗時フォールバックを保証。
     *
     * MT 8.8.2 など一部バージョンでは、ダイアログ内アップロード（js_upload_file）の
     * レスポンスに asset.url が含まれず、アップロード結果パネルの <a> も href 空になる。
     * その結果 transferAssetData が url を取得できず、multiField のプレビューが
     * `{#if url}` 条件で表示されない。url が空のときはここで Data API から補完する。
     *
     * @static
     * @returns {Promise<string>} アセットの URL（失敗時は fallbackUrl）
     */
    static async fetchAssetUrlWithFallback(apiUrl, siteId, assetId, fallbackUrl) {
        const TIMEOUT_MS = 5000;
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
        const assetApiUrl = `${apiUrl}/sites/${siteId}/assets/${assetId}`;
        try {
            const res = await fetch(assetApiUrl, { signal: ctrl.signal });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            if (data.error || !data.url) {
                throw new Error((data.error && data.error.message) || 'asset url not returned');
            }
            return data.url;
        } catch {
            return fallbackUrl;
        } finally {
            clearTimeout(timer);
        }
    }

    /**
     * モーダルが非表示になった際のハンドラー。
     *
     * 設計上の不変式（race condition 防止）:
     *  1. await を跨いで DOM 参照を持ち越さない（Vue/Svelte の再描画で detached になる対策）
     *  2. メディア一覧経由の場合は metadata_json のサムネイルを最優先（Data API を呼ばない）
     *  3. Data API へ問い合わせる場合は必ずタイムアウト＆フォールバック付き
     *  4. updateItemIdButton.click() は reactivity 反映後（requestAnimationFrame 後）に発火
     *
     * @static
     */
    static async handleModalHidden() {
        const assetFieldObj = window.top.mtappVars.__system.assetField;
        if (assetFieldObj.assetId === '') return;
        if (!assetFieldObj.fieldId) {
            // アセットIDは転送済みなのに反映先フィールドが不明 = 異常系。無言で捨てない。
            console.warn(
                `[AssetField] 反映先フィールドが不明のためアセット(id=${assetFieldObj.assetId})を反映できませんでした。`
            );
            return;
        }

        const fieldId = assetFieldObj.fieldId;
        const assetId = assetFieldObj.assetId;
        const assetType = assetFieldObj.assetType;
        const assetDetails = assetFieldObj.assetDetails;
        const thumbnailWidth = assetFieldObj.thumbnailWidth || 120;
        const thumbnailHeight = assetFieldObj.thumbnailHeight || 120;

        try {
            // ========== 1. データ収集（await はここまで） ==========
            // URL は通常 assetDetails から取れる（transferAssetData がセットする）が、
            // MT 8.8.2 等ではアップロード結果に url が無く空になる。その場合は Data API で補完する。
            // url が空のままだと multiField のプレビューが `{#if url}` で表示されないため必須。
            let url = (assetDetails && assetDetails.url) || '';
            if (!url) {
                const apiUrl = getDataApiUrl();
                const siteId = window.mtappVars.blog_id;
                url = await AssetField.fetchAssetUrlWithFallback(apiUrl, siteId, assetId, '');
            }

            // サムネイル URL を決定（画像のときのみ）
            let thumbnail = '';
            if (assetType === 'image') {
                // ① metadata_json に thumbnail があれば即採用（Data API 不要）
                const fromMeta = AssetField.pickThumbFromMetadata(assetDetails, thumbnailWidth, thumbnailHeight);
                if (fromMeta) {
                    thumbnail = fromMeta;
                } else {
                    // ② Data API で指定寸法のサムネイルを取得（サイズ統一）。
                    //    タイムアウト・失敗時は、アップロード結果の固定サムネイル（あれば）→ url の順で採用。
                    const apiUrl = getDataApiUrl();
                    const siteId = window.mtappVars.blog_id;
                    const thumbFallback = (assetDetails && assetDetails.thumbnail_url) || url;
                    thumbnail = await AssetField.fetchThumbnailWithFallback(
                        apiUrl, siteId, assetId, thumbnailWidth, thumbnailHeight, thumbFallback
                    );
                }
            }

            // ========== 2. DOM 参照取得 → 値設定（完全同期） ==========
            const targetIdEl = document.getElementById(fieldId);
            if (!targetIdEl) {
                // 反映先の input が見つからない（コンポーネントの再描画等）= 無言で失敗させない
                console.warn(`[AssetField] 反映先要素 #${fieldId} が見つからないため、アセット(id=${assetId})を反映できませんでした。`);
                return;
            }
            const targetUrlEl = document.getElementById(fieldId + '-url');
            const targetThumbEl = document.getElementById(fieldId + '-thumb');

            AssetField.setElementValue(targetIdEl, assetId);
            if (targetUrlEl) AssetField.setElementValue(targetUrlEl, removeHost(url));
            if (targetThumbEl) AssetField.setElementValue(targetThumbEl, removeHost(thumbnail));
            if (thumbnail) {
                window.dispatchEvent(new CustomEvent('mtapp:thumbnailApplied', { detail: { url: thumbnail, assetId } }));
            }

            // ========== 3. reactivity 反映後に updateItemIdButton をクリック ==========
            const updateBtn = targetIdEl.previousElementSibling;
            requestAnimationFrame(() => {
                if (updateBtn instanceof HTMLElement) updateBtn.click();
            });
        } catch (e) {
            console.error('[AssetField] handleModalHidden error:', e);
        } finally {
            delete assetFieldObj.fieldId;
        }
    }

    //----------------------------------------
    // インスタンスプロパティ
    //----------------------------------------

    id = '';
    type = 'image';
    handle = '';

    //----------------------------------------
    // インスタンスメソッド
    //----------------------------------------

    /**
     * アセットを選択するためのメソッド。
     * 指定されたIDとタイプに基づいてアセットを選択し、適切なURLをセットしてクリックイベントをトリガーします。
     * @method
     * @this AssetField
     */
    select() {
        window.mtappVars.__system.assetField.fieldId = this.id;
        window.mtappVars.__system.assetField.assetType = this.type;
        window.mtappVars.__system.assetField.thumbnailWidth = this.thumbnailWidth;
        window.mtappVars.__system.assetField.thumbnailHeight = this.thumbnailHeight;

        const triggerElement = document.getElementById('mtapp-asset-field');
        const href = triggerElement.getAttribute('href');
        let url;
        if (href.startsWith('http://') || href.startsWith('https://')) {
            // hrefが完全なURLの場合
            url = new URL(href);
        } else {
            // hrefが相対URLの場合
            url = new URL(href, window.location.origin);
        }

        // パラメータの変更や削除の処理
        if (this.type === 'image') {
            url.searchParams.set('filter', 'class');
            url.searchParams.set('filter_val', 'image');
            url.searchParams.set('require_type', 'image');
        } else {
            url.searchParams.delete('filter');
            url.searchParams.delete('filter_val');
            url.searchParams.delete('require_type');
        }
        url.searchParams.set('mtapp_mf_handle', this.handle);

        // 変更されたURLをhref属性に設定
        triggerElement.setAttribute('href', url.toString());
        triggerElement.click();
        return false;
    }

    /**
     * Removes an asset with a specified ID.
     * @param {string} id - ID of the asset to be removed.
     */
    remove() {
        const idField = document.getElementById(this.id);
        if (idField) {
            AssetField.setElementValue(idField, '');
        }

        const thumbField = document.getElementById(this.id + '-thumb');
        if (thumbField) {
            AssetField.setElementValue(thumbField, '');
        }

        const urlField = document.getElementById(this.id + '-url');
        if (urlField) {
            AssetField.setElementValue(urlField, '');
        }
    }
}
